
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  ArrowLeft, 
  ArrowRight, 
  PlusCircle, 
  CalendarIcon,
  Download,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  Sliders
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, addMonths, subMonths } from "date-fns";
import { pt } from "date-fns/locale";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/components/ui/use-toast";

interface FluxoTabContentProps {
  periodoSelecionado: string;
}

const FluxoTabContent: React.FC<FluxoTabContentProps> = ({ periodoSelecionado }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [date, setDate] = useState<Date | undefined>(new Date());
  
  const handleDateSelected = (date?: Date) => {
    if (date) {
      setDate(date);
      toast({
        title: "Período selecionado",
        description: `Selecionado: ${format(date, "MMMM 'de' yyyy", { locale: pt })}`,
      });
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-normal text-gray-700">Fluxo de Caixa Detalhado</h2>
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="text-gray-400 hover:text-gray-600 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-1 text-sm">
                <CalendarIcon className="h-4 w-4 text-blue-400" />
                <span className="text-gray-600">{periodoSelecionado}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelected}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button variant="ghost" className="text-gray-400 hover:text-gray-600 p-2">
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
      
      <div className="flex justify-start mb-4 items-center gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 px-2 text-gray-600 border-gray-200"
        >
          <RefreshCw className="h-4 w-4 mr-1 text-blue-400" /> Atualizar
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 px-2 text-gray-600 border-gray-200"
            >
              <Sliders className="h-4 w-4 mr-1 text-blue-400" /> Visualização
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Opções de visualização</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              Visualizar por semana
            </DropdownMenuItem>
            <DropdownMenuItem>
              Visualizar por mês
            </DropdownMenuItem>
            <DropdownMenuItem>
              Visualizar por trimestre
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              Agrupar por categoria
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Button 
          variant="outline" 
          size="sm" 
          className="h-8 px-2 text-gray-600 border-gray-200"
        >
          <Download className="h-4 w-4 mr-1 text-blue-400" /> Exportar
        </Button>
      </div>
      
      <div className="flex flex-col items-center justify-center p-10 border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
        <BarChart3 className="h-16 w-16 mb-4 opacity-30" />
        <p className="mb-2 text-lg font-normal">Nenhum dado disponível</p>
        <p className="text-sm mb-6 text-center">Selecione um período e adicione transações para visualizar seu fluxo de caixa</p>
        <div className="flex gap-4">
          <Button 
            variant="outline" 
            className="border-gray-200 text-blue-500"
            onClick={() => navigate("/financeiro/form")}
          >
            <PlusCircle className="h-4 w-4 mr-2" /> Criar transação
          </Button>
          <Button className="bg-blue-500 hover:bg-blue-600 text-white">
            <CalendarIcon className="h-4 w-4 mr-2" /> Selecionar período
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FluxoTabContent;
